'use client';
import React, { useCallback, useEffect, useState } from 'react';
import { AspectRatio, Box, HStack, Spinner, Button } from '@chakra-ui/react';
import Image from 'next/image';
import { QuestionIcon } from '@/_assets/icon-components';
import useEmblaCarousel from 'embla-carousel-react';

const SliderMobile = ({ list }: { list: string[] }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (list.length > 0) {
      setIsLoading(false);
    }
  }, [list]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  if (!list?.length) return null;
  if (isLoading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        h={'80vh'}
      >
        <Spinner color='blue.500' size='lg' />
      </Box>
    );
  }

  return (
    <Box position='relative'>
      {/* Carousel */}
      <Box className='embla' overflow='hidden' ref={emblaRef}>
        <Box className='embla__container' display='flex'>
          {list.map((item, idx) => {
            const imageUrl = item?.includes(process.env.BASE_URL!)
              ? item
              : process.env.BASE_URL + item;

            return (
              <Box
                key={item + idx}
                className='embla__slide'
                flexShrink={0}
                flexGrow={0}
                flexBasis='100%'
                minWidth='0'
              >
                <AspectRatio
                  display='grid'
                  placeItems='center'
                  position='relative'
                  ratio={1 / 1}
                >
                  {item ? (
                    <Image
                      src={imageUrl}
                      priority={idx === 0}
                      style={{
                        objectFit: 'cover',
                        width: '100%',
                        height: '100%',
                      }}
                      loading={idx === 0 ? 'eager' : 'lazy'}
                      alt='banner'
                      fill
                    />
                  ) : (
                    <Box>
                      <QuestionIcon />
                    </Box>
                  )}
                </AspectRatio>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Dots Pagination */}
      {list.length > 1 && (
        <HStack
          position='absolute'
          bottom='10px'
          left='50%'
          transform='translateX(-50%)'
          gap={2}
        >
          {list.map((_, idx) => (
            <Button
              key={idx}
              onClick={() => scrollTo(idx)}
              minW='16px'
              minH='16px'
              width={selectedIndex === idx ? '10px' : '6px'}
              height={selectedIndex === idx ? '10px' : '6px'}
              borderRadius='full'
              bg={selectedIndex === idx ? 'primary' : 'gray.200'}
              transition='all 0.3s'
              p={0}
            />
          ))}
        </HStack>
      )}
    </Box>
  );
};

export default SliderMobile;
